"use client";

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child component trees and displays
 * a fallback UI instead of crashing the entire app.
 *
 * React requires error boundaries to be class components.
 */

import { Component, type ReactNode } from "react";
import { reportError } from "@/lib/reportError";
import { ErrorTriangleOutlineIcon } from "@/components/icons/ErrorTriangleOutlineIcon";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error
    console.error("[ErrorBoundary] Caught error:", error);
    console.error("[ErrorBoundary] Error info:", errorInfo);

    // Funnel to PostHog + the server diag endpoint (with identity).
    reportError(error, {
      boundary: "ErrorBoundary",
      componentStack: errorInfo.componentStack ?? undefined,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-bg-base">
          <div className="max-w-md w-full rounded-20 p-8 text-center animate-fadeIn-4 bg-bg-elevated border border-solid border-border-default shadow-lg">
            {/* Error icon */}
            <div className="w-14 h-14 rounded-20 flex items-center justify-center mx-auto mb-5 bg-palette-red/10">
              <ErrorTriangleOutlineIcon className="w-7 h-7 text-status-error" />
            </div>

            <h1 className="text-5 font-semibold mb-2 text-fg-default">
              Something went wrong
            </h1>
            <p className="text-3.5 mb-5 text-fg-muted">
              An unexpected error occurred. You can try refreshing the page or
              continuing.
            </p>

            {this.state.error && (
              <div className="rounded-16 p-4 mb-5 text-left bg-code-bg border border-solid border-code-border">
                <p className="text-3 font-mono text-4 break-all text-status-error">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-5 py-2.5 rounded-16 text-3.5 font-medium transition-all bg-bg-muted text-fg-default border border-solid border-border-default"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--bg-elevated)";
                  e.currentTarget.style.borderColor = "var(--border-muted)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--bg-muted)";
                  e.currentTarget.style.borderColor = "var(--border-default)";
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 rounded-16 text-3.5 font-medium transition-all bg-accent-primary text-[white]"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "var(--accent-primary-hover)";
                  e.currentTarget.style.boxShadow = "var(--shadow-glow)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--accent-primary)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-friendly wrapper for error boundary
 * Use this when you need to catch errors in a specific component tree
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary fallback={fallback}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `withErrorBoundary(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? "Component"
  })`;

  return WithErrorBoundary;
}
